import React, { useState, useEffect } from 'react';
import { Customer, CustomerCreate, Controller, Supervisor, Server } from '../types';
import { apiService } from '../services/api';

interface CustomerFormProps {
  initialData?: Customer | null;
  onSubmit: (data: CustomerCreate) => void;
  onCancel: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<CustomerCreate>({
    customer_type: initialData?.customer_type || 'controller',
    controller_id: initialData?.controller_id || undefined,
    supervisor_id: initialData?.supervisor_id || undefined,
    customer_name: initialData?.customer_name || '',
    customer_contact_name: initialData?.customer_contact_name || '',
    customer_contact_role: initialData?.customer_contact_role || '',
    customer_address_street: initialData?.customer_address_street || '',
    customer_address_postcode: initialData?.customer_address_postcode || '',
    customer_address_city: initialData?.customer_address_city || '',
    customer_address_country: initialData?.customer_address_country || '',
    customer_address_state: initialData?.customer_address_state || '',
    customer_telephone_number: initialData?.customer_telephone_number || '',
    customer_email_address: initialData?.customer_email_address || '',
    customer_tax_id: initialData?.customer_tax_id || '',
    customer_vat_number: initialData?.customer_vat_number || '',
    customer_birth_date: initialData?.customer_birth_date 
      ? new Date(initialData.customer_birth_date).toISOString().split('T')[0]
      : '',
    service_description: initialData?.service_description || '',
    plan_name: initialData?.plan_name || '',
    billing_period_start: initialData?.billing_period_start 
      ? new Date(initialData.billing_period_start).toISOString().split('T')[0]
      : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    billing_period_end: initialData?.billing_period_end 
      ? new Date(initialData.billing_period_end).toISOString().split('T')[0]
      : '',
    unit_price: initialData?.unit_price || 0,
    quantity: initialData?.quantity || 1,
    subtotal: initialData?.subtotal || 0,
    tax_rate: initialData?.tax_rate || 0,
    tax_amount: initialData?.tax_amount || 0,
    total_amount: initialData?.total_amount || 0,
    payment_status: initialData?.payment_status || 'pending',
    payment_date: initialData?.payment_date 
      ? new Date(initialData.payment_date).toISOString().split('T')[0]
      : '',
    payment_method: initialData?.payment_method || '',
    payment_reference: initialData?.payment_reference || '',
    currency: initialData?.currency || 'EUR',
    language: initialData?.language || 'en',
    notes: initialData?.notes || '',
    terms_and_conditions: initialData?.terms_and_conditions || ''
  });

  const [controllers, setControllers] = useState<Controller[]>([]);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [controllersData, supervisorsData, serversData] = await Promise.all([
        apiService.getAllControllers(),
        apiService.getAllSupervisors(),
        apiService.getAllServers()
      ]);
      
      setControllers(controllersData);
      setSupervisors(supervisorsData);
      setServers(serversData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }

    // Enable controller/supervisor validation now that we have dropdowns
    if (formData.customer_type === 'controller' && !formData.controller_id) {
      newErrors.controller_id = 'Controller is required for controller type customers';
    }

    if (formData.customer_type === 'supervisor' && !formData.supervisor_id) {
      newErrors.supervisor_id = 'Supervisor is required for supervisor type customers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Helper function to format date without timezone conversion
      const formatDateForBackend = (dateString: string | undefined): string | undefined => {
        if (!dateString) return undefined;
        // The date string is already in YYYY-MM-DD format from the input field
        // We append T00:00:00 to make it a valid ISO string without timezone conversion
        return `${dateString}T00:00:00`;
      };

      // Convert dates to ISO format for the backend without timezone conversion
      const submitData = {
        ...formData,
        billing_period_start: formatDateForBackend(formData.billing_period_start),
        billing_period_end: formatDateForBackend(formData.billing_period_end),
        payment_date: formatDateForBackend(formData.payment_date),
        customer_birth_date: formatDateForBackend(formData.customer_birth_date)
      };
      
      onSubmit(submitData);
    }
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'pt-br', name: 'Portuguese (Brazil)' },
    { code: 'nl', name: 'Dutch' },
    { code: 'pl', name: 'Polish' },
    { code: 'ru', name: 'Russian' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' }
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Customer Type *</label>
            <select
              name="customer_type"
              value={formData.customer_type}
              onChange={handleChange}
              className="form-control"
            >
              <option value="controller">Controller</option>
              <option value="supervisor">Supervisor</option>
            </select>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Language *</label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              className="form-control"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Server</label>
            <select
              name="selectedServerId"
              value={selectedServerId}
              onChange={(e) => setSelectedServerId(e.target.value)}
              className="form-control"
              disabled={loading}
            >
              <option value="">Select a server (optional)</option>
              {servers.map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name} ({server.host})
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-col">
          {formData.customer_type === 'controller' ? (
            <div className="form-group">
              <label className="form-label">Controller *</label>
              <select
                name="controller_id"
                value={formData.controller_id || ''}
                onChange={handleChange}
                className={`form-control ${errors.controller_id ? 'error' : ''}`}
                disabled={loading}
              >
                <option value="">Select a controller</option>
                {controllers
                  .filter(controller => !selectedServerId || controller.server_id.toString() === selectedServerId)
                  .map((controller) => (
                    <option key={controller.id} value={controller.id}>
                      {controller.controller_name} (ID: {controller.controller_id})
                    </option>
                  ))}
              </select>
              {errors.controller_id && <div className="form-error">{errors.controller_id}</div>}
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Supervisor *</label>
              <select
                name="supervisor_id"
                value={formData.supervisor_id || ''}
                onChange={handleChange}
                className={`form-control ${errors.supervisor_id ? 'error' : ''}`}
                disabled={loading}
              >
                <option value="">Select a supervisor</option>
                {supervisors
                  .filter(supervisor => !selectedServerId || supervisor.server_id.toString() === selectedServerId)
                  .map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>
                      {supervisor.display_name || `Supervisor ${supervisor.supervisor_id}`} (ID: {supervisor.supervisor_id})
                    </option>
                  ))}
              </select>
              {errors.supervisor_id && <div className="form-error">{errors.supervisor_id}</div>}
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Customer Name *</label>
        <input
          type="text"
          name="customer_name"
          value={formData.customer_name}
          onChange={handleChange}
          className={`form-control ${errors.customer_name ? 'error' : ''}`}
          placeholder="Enter customer name"
        />
        {errors.customer_name && <div className="form-error">{errors.customer_name}</div>}
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Contact Name</label>
            <input
              type="text"
              name="customer_contact_name"
              value={formData.customer_contact_name}
              onChange={handleChange}
              className="form-control"
              placeholder="Contact person name"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Contact Role</label>
            <input
              type="text"
              name="customer_contact_role"
              value={formData.customer_contact_role}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g., Manager, Director"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="customer_email_address"
              value={formData.customer_email_address}
              onChange={handleChange}
              className="form-control"
              placeholder="customer@example.com"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input
              type="tel"
              name="customer_telephone_number"
              value={formData.customer_telephone_number}
              onChange={handleChange}
              className="form-control"
              placeholder="+1234567890"
            />
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Address Information</h3>
      
      <div className="form-group">
        <label className="form-label">Street Address</label>
        <input
          type="text"
          name="customer_address_street"
          value={formData.customer_address_street}
          onChange={handleChange}
          className="form-control"
          placeholder="123 Main Street"
        />
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">City</label>
            <input
              type="text"
              name="customer_address_city"
              value={formData.customer_address_city}
              onChange={handleChange}
              className="form-control"
              placeholder="City"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Postal Code</label>
            <input
              type="text"
              name="customer_address_postcode"
              value={formData.customer_address_postcode}
              onChange={handleChange}
              className="form-control"
              placeholder="12345"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">State/Province</label>
            <input
              type="text"
              name="customer_address_state"
              value={formData.customer_address_state}
              onChange={handleChange}
              className="form-control"
              placeholder="State"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Country</label>
            <input
              type="text"
              name="customer_address_country"
              value={formData.customer_address_country}
              onChange={handleChange}
              className="form-control"
              placeholder="Country"
            />
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Tax Information</h3>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Tax ID</label>
            <input
              type="text"
              name="customer_tax_id"
              value={formData.customer_tax_id}
              onChange={handleChange}
              className="form-control"
              placeholder="Tax identification number"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">VAT Number</label>
            <input
              type="text"
              name="customer_vat_number"
              value={formData.customer_vat_number}
              onChange={handleChange}
              className="form-control"
              placeholder="VAT registration number"
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Birth Date</label>
        <input
          type="date"
          name="customer_birth_date"
          value={formData.customer_birth_date}
          onChange={handleChange}
          className="form-control"
        />
      </div>

      <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Service & Billing Information</h3>

      <div className="form-group">
        <label className="form-label">Service Description</label>
        <textarea
          name="service_description"
          value={formData.service_description}
          onChange={handleChange}
          className="form-control"
          rows={3}
          placeholder="Description of services provided..."
        />
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Plan Name</label>
            <input
              type="text"
              name="plan_name"
              value={formData.plan_name}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g., Basic Plan, Premium Plan"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Currency</label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="form-control"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="BRL">BRL</option>
              <option value="CAD">CAD</option>
              <option value="CHF">CHF</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Billing Period Start</label>
            <input
              type="date"
              name="billing_period_start"
              value={formData.billing_period_start}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Billing Period End</label>
            <input
              type="date"
              name="billing_period_end"
              value={formData.billing_period_end}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Pricing Information</h3>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Unit Price</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="unit_price"
              value={formData.unit_price}
              onChange={handleChange}
              className="form-control"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Quantity</label>
            <input
              type="number"
              min="1"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className="form-control"
              placeholder="1"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Subtotal</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="subtotal"
              value={formData.subtotal}
              onChange={handleChange}
              className="form-control"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Tax Rate (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              name="tax_rate"
              value={formData.tax_rate}
              onChange={handleChange}
              className="form-control"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Tax Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="tax_amount"
              value={formData.tax_amount}
              onChange={handleChange}
              className="form-control"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Total Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              className="form-control"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Payment Information</h3>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Payment Status</label>
            <select
              name="payment_status"
              value={formData.payment_status}
              onChange={handleChange}
              className="form-control"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
              <option value="partial">Partial</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Payment Date</label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Select payment method</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="credit_card">Credit Card</option>
              <option value="paypal">PayPal</option>
              <option value="stripe">Stripe</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="boleto">Boleto</option>
              <option value="pix">PIX</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Payment Reference</label>
            <input
              type="text"
              name="payment_reference"
              value={formData.payment_reference}
              onChange={handleChange}
              className="form-control"
              placeholder="Transaction ID, reference number, etc."
            />
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Additional Information</h3>

      <div className="form-group">
        <label className="form-label">Terms and Conditions</label>
        <textarea
          name="terms_and_conditions"
          value={formData.terms_and_conditions}
          onChange={handleChange}
          className="form-control"
          rows={4}
          placeholder="Terms and conditions for this customer..."
        />
      </div>

      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className="form-control"
          rows={3}
          placeholder="Additional notes..."
        />
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Loading...' : (initialData ? 'Update' : 'Create')}
        </button>
      </div>
    </form>
  );
};

export default CustomerForm;